/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// @mui material components
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import { Theme } from "@mui/material/styles";
import Button from "@mui/material/Button";
import AssessmentIcon from "@mui/icons-material/Assessment";

// @mui icons
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";

// Material Dashboard 2 PRO React TS components
import MDBox from "dashboard-template/components/MDBox";
import MDTypography from "dashboard-template/components/MDTypography";
import MDButton from "dashboard-template/components/MDButton";

// Custom styles for the Configurator
import ConfiguratorRoot from "dashboard-template/examples/Configurator/ConfiguratorRoot";
import { useTranslation } from "react-i18next";
import { UserRolesEnum, HeaderEnum, UserInterface } from "@malmur-technologies/medicalsystem-shared";
import { SidenavColorEnum } from "@malmur-technologies/medicalsystem-shared";
import { selectUserRoles, selectCurrentUser } from "store/slices/auth.slice";
import { authAction } from "store/store";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setOpenConfigurator,
  setTransparentSidenav,
  setWhiteSidenav,
  setMiniSidenav,
  setFixedNavbar,
  setSidenavColor,
  setDarkMode,
} from "context/ThemeContext";
import SwaggerButton from "components/generals/SwaggerButton";
import { EntityPermissions, PermissionsUser } from "types";
import PermissionWrapper from "components/generals/PermissionWrapper";

function Configurator(): JSX.Element {
  const [controller, themeDispatch] = useMaterialUIController();
  const {
    openConfigurator,
    fixedNavbar,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    miniSidenav,
    darkMode,
  } = controller;
  const [disabled, setDisabled] = useState<boolean>(false);
  const { t } = useTranslation();
  const userRoles = useSelector(selectUserRoles);
  const currentUser = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const [_isLoading, _error, sendRequest] = useHttp();
  const reduxDispatch = useDispatch();

  const saveColorToProfile = async (color: string) => {
    if (!currentUser?.id) return;

    const updatedProfile = {
      ...(currentUser.profile || {}),
      sidenavColor: color,
      transparentSidenav: transparentSidenav,
      whiteSidenav: whiteSidenav,
    };

    const updatedUser: UserInterface = {
      ...currentUser,
      profile: updatedProfile,
    };

    try {
      await sendRequest<UserInterface>(
        CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
        (response) => {
          if (response) {
            reduxDispatch(authAction.storeUser(response));
          }
        },
        'PUT',
        JSON.stringify(updatedUser),
        [{ header: HeaderEnum.USER_ID, value: currentUser.id }]
      );
    } catch (error) {
      // Error handling
    }
  };

  const saveSidenavTypeToProfile = async (transparent: boolean, white: boolean) => {
    if (!currentUser?.id) return;

    const updatedProfile = {
      ...(currentUser.profile || {}),
      transparentSidenav: transparent,
      whiteSidenav: white,
    };

    const updatedUser: UserInterface = {
      ...currentUser,
      profile: updatedProfile,
    };

    try {
      await sendRequest<UserInterface>(
        CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
        (response) => {
          if (response) {
            reduxDispatch(authAction.storeUser(response));
          }
        },
        'PUT',
        JSON.stringify(updatedUser),
        [{ header: HeaderEnum.USER_ID, value: currentUser.id }]
      );
    } catch (error) {
      // Error handling
    }
  };


  const isAdminOrRoot = userRoles.includes(UserRolesEnum.ADMIN) || userRoles.includes(UserRolesEnum.ROOT);

  const sidenavColors = [
    SidenavColorEnum.PRIMARY,
    SidenavColorEnum.DARK,
    SidenavColorEnum.INFO,
    SidenavColorEnum.SUCCESS,
    SidenavColorEnum.WARNING,
    SidenavColorEnum.ERROR,
  ];

  useEffect(() => {
    function handleDisabled() {
      return setDisabled(false);
    }

    window.addEventListener("resize", handleDisabled);
    handleDisabled();
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  const handleCloseConfigurator = () => setOpenConfigurator(themeDispatch, false);
  const handleTransparentSidenav = async () => {
    setTransparentSidenav(themeDispatch, true);
    setWhiteSidenav(themeDispatch, false);
    await saveSidenavTypeToProfile(true, false);
  };
  const handleWhiteSidenav = async () => {
    setWhiteSidenav(themeDispatch, true);
    setTransparentSidenav(themeDispatch, false);
    await saveSidenavTypeToProfile(false, true);
  };
  const handleDarkSidenav = async () => {
    setWhiteSidenav(themeDispatch, false);
    setTransparentSidenav(themeDispatch, false);
    await saveSidenavTypeToProfile(false, false);
  };
  const handleMiniSidenav = async () => {
    const newMiniSidenav = !miniSidenav;
    setMiniSidenav(themeDispatch, newMiniSidenav);
    await savePreferenceToProfile('miniSidenav', newMiniSidenav);
  };
  const handleFixedNavbar = async () => {
    const newFixedNavbar = !fixedNavbar;
    setFixedNavbar(themeDispatch, newFixedNavbar);
    await savePreferenceToProfile('fixedNavbar', newFixedNavbar);
  };
  const handleDarkMode = async () => {
    const newDarkMode = !darkMode;
    setDarkMode(themeDispatch, newDarkMode);
    await saveDarkModeToProfile(newDarkMode);
  };

  const saveDarkModeToProfile = async (isDarkMode: boolean) => {
    if (!currentUser) return;

    const updatedProfile = {
      ...(currentUser.profile || {}),
      darkMode: isDarkMode,
      sidenavColor,
      transparentSidenav,
      whiteSidenav,
    };

    const updatedUser = {
      ...currentUser,
      profile: updatedProfile,
    };

    try {
      await sendRequest<UserInterface>(
        CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
        (response) => {
          if (response) {
            reduxDispatch(authAction.storeUser(response));
          }
        },
        'PUT',
        JSON.stringify(updatedUser),
        [{ header: HeaderEnum.USER_ID, value: currentUser.id }]
      );
    } catch (error) {
      // Error handling
    }
  };

  const savePreferenceToProfile = async (key: string, value: any) => {
    if (!currentUser) return;

    const updatedProfile = {
      ...(currentUser.profile || {}),
      [key]: value,
    };

    const updatedUser = {
      ...currentUser,
      profile: updatedProfile,
    };

    try {
      await sendRequest<UserInterface>(
        CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
        (response) => {
          if (response) {
            reduxDispatch(authAction.storeUser(response));
          }
        },
        'PUT',
        JSON.stringify(updatedUser),
        [{ header: HeaderEnum.USER_ID, value: currentUser.id }]
      );
    } catch (error) {
      // Error handling
    }
  };


  const sidenavTypeButtonsStyles = ({
    functions: { pxToRem },
    palette: { white, dark, background },
    borders: { borderWidth },
  }: Theme | any) => ({
    height: pxToRem(39),
    background: darkMode ? background.sidenav : white.main,
    color: darkMode ? white.main : dark.main,
    border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? background.sidenav : white.main,
      color: darkMode ? white.main : dark.main,
      border: `${borderWidth[1]} solid ${darkMode ? white.main : dark.main}`,
    },
  });

  // sidenav type active button styles
  const sidenavTypeActiveButtonStyles = ({
    functions: { pxToRem, linearGradient },
    palette: { white, gradients, background },
  }: Theme | any) => ({
    height: pxToRem(39),
    background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
    color: darkMode ? background.sidenav : white.main,

    "&:hover, &:focus, &:focus:not(:hover)": {
      background: darkMode ? white.main : linearGradient(gradients.dark.main, gradients.dark.state),
      color: darkMode ? background.sidenav : white.main,
    },
  });

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={4}
        pb={0.5}
        px={3}
      >
        <MDBox>
          <MDTypography variant="h5">{t("userProfilePage.dashboardConfigurator")}</MDTypography>
        </MDBox>

        <CloseIcon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={handleCloseConfigurator}
        />
      </MDBox>

      <Divider />

      <MDBox pt={0.5} pb={3} px={3}>
        <MDBox>
          <MDTypography variant="h6">{t("userProfilePage.sidenavColors")}</MDTypography>

          <MDBox mb={0.5}>
            {sidenavColors.map((color) => (
              <IconButton
                key={color}
                sx={({
                  borders: { borderWidth },
                  palette: { white, dark, background },
                  transitions,
                }: Theme | any) => ({
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  border: `${borderWidth[1]} solid ${darkMode ? background.sidenav : white.main}`,
                  borderColor: () => {
                    let borderColorValue = sidenavColor === color && dark.main;

                    if (darkMode && sidenavColor === color) {
                      borderColorValue = white.main;
                    }

                    return borderColorValue;
                  },
                  transition: transitions.create("border-color", {
                    easing: transitions.easing.sharp,
                    duration: transitions.duration.shorter,
                  }),
                  backgroundImage: ({ functions: { linearGradient }, palette: { gradients } }) =>
                    linearGradient(gradients[color].main, gradients[color].state),

                  "&:not(:last-child)": {
                    mr: 1,
                  },

                  "&:hover, &:focus, &:active": {
                    borderColor: darkMode ? white.main : dark.main,
                  },
                })}
                onClick={async () => {
                  setSidenavColor(themeDispatch, color);
                  await saveColorToProfile(color);
                }}
              />
            ))}
          </MDBox>
        </MDBox>

        <MDBox mt={3} lineHeight={1}>
          <MDTypography variant="h6">{t("userProfilePage.sidenavType")}</MDTypography>
          <MDTypography variant="button" color="text">
            {t("userProfilePage.chooseBetweenDifferentSidenavTypes")}
          </MDTypography>

          <MDBox
            sx={{
              display: "flex",
              mt: 2,
              mr: 1,
            }}
          >
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleDarkSidenav}
              disabled={disabled}
              fullWidth
              sx={
                !transparentSidenav && !whiteSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              {t("userProfilePage.dark")}
            </MDButton>
            <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleTransparentSidenav}
                disabled={disabled}
                fullWidth
                sx={
                  transparentSidenav && !whiteSidenav
                    ? sidenavTypeActiveButtonStyles
                    : sidenavTypeButtonsStyles
                }
              >
                {t("userProfilePage.transparent")}
              </MDButton>
            </MDBox>
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleWhiteSidenav}
              disabled={disabled}
              fullWidth
              sx={
                whiteSidenav && !transparentSidenav
                  ? sidenavTypeActiveButtonStyles
                  : sidenavTypeButtonsStyles
              }
            >
              {t("userProfilePage.white")}
            </MDButton>
          </MDBox>
        </MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          lineHeight={1}
        >
          <MDTypography variant="h6">{t("userProfilePage.navbarFixed")}</MDTypography>

          <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography variant="h6">{t("userProfilePage.sidenavMini")}</MDTypography>

          <Switch checked={miniSidenav} onChange={handleMiniSidenav} />
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography variant="h6">{t("userProfilePage.lightDark")}</MDTypography>

          <Switch checked={darkMode} onChange={handleDarkMode} />
        </MDBox>
        <Divider />

        <PermissionWrapper
          requiredPermissions={[PermissionsUser[EntityPermissions.SWAGGER].VIEW]}
        >
          <SwaggerButton variant="outlined" color="info" size="small" />
        </PermissionWrapper>

        {isAdminOrRoot && (
          <>
            <MDBox mt={3} mb={2}>
              <MDButton
                variant="contained"
                color="info"
                startIcon={<AssessmentIcon />}
                fullWidth
                onClick={() => {
                  navigate('/activity-logs');
                  setOpenConfigurator(themeDispatch, false);
                }}
              >
                {t('activityLogs.title')}
              </MDButton>
            </MDBox>
            <Divider />
            <MDBox mt={3} mb={2}>
              <MDButton
                id="cy-general-settings-button"
                variant="contained"
                color="primary"
                startIcon={<SettingsIcon />}
                fullWidth
                onClick={() => {
                  navigate('/general-settings');
                  setOpenConfigurator(themeDispatch, false);
                }}
              >
                {t('userProfilePage.generalSystemSettings')}
              </MDButton>
            </MDBox>
            <Divider />
          </>
        )}
      </MDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
